import { useEffect, useCallback, useRef } from 'react';

// Screen reader announcements
export function useAnnounce() {
  const announcerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create announcer element if it doesn't exist
    if (!document.getElementById('aria-announcer')) {
      const announcer = document.createElement('div');
      announcer.id = 'aria-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      document.body.appendChild(announcer);
      announcerRef.current = announcer;
    } else {
      announcerRef.current = document.getElementById('aria-announcer') as HTMLDivElement;
    }

    return () => {
      // Clean up announcer on unmount
      if (announcerRef.current && announcerRef.current.parentNode) {
        announcerRef.current.parentNode.removeChild(announcerRef.current);
      }
    };
  }, []);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (announcerRef.current) {
      announcerRef.current.setAttribute('aria-live', priority);
      announcerRef.current.textContent = message;
      
      // Clear after announcement
      setTimeout(() => {
        if (announcerRef.current) {
          announcerRef.current.textContent = '';
        }
      }, 1000);
    }
  }, []);

  return announce;
}

// Reduced motion preference
export function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
}

// High contrast mode detection
export function usePrefersHighContrast() {
  const [prefersHighContrast, setPrefersHighContrast] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    setPrefersHighContrast(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersHighContrast(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersHighContrast;
}

// Color scheme preference
export function usePrefersColorScheme() {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setColorScheme(mediaQuery.matches ? 'dark' : 'light');

    const handleChange = (e: MediaQueryListEvent) => {
      setColorScheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return colorScheme;
}

// ARIA labels and descriptions
export function useAriaLabels(elementRef: React.RefObject<HTMLElement>) {
  const setLabel = useCallback((label: string) => {
    if (elementRef.current) {
      elementRef.current.setAttribute('aria-label', label);
    }
  }, [elementRef]);

  const setDescription = useCallback((description: string) => {
    if (elementRef.current) {
      const id = `desc-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create description element
      const descElement = document.createElement('span');
      descElement.id = id;
      descElement.className = 'sr-only';
      descElement.textContent = description;
      
      // Add to DOM
      elementRef.current.appendChild(descElement);
      elementRef.current.setAttribute('aria-describedby', id);
    }
  }, [elementRef]);

  return { setLabel, setDescription };
}

// Live region hook
export function useLiveRegion() {
  const regionRef = useRef<HTMLDivElement | null>(null);

  const createRegion = useCallback((
    type: 'polite' | 'assertive' | 'off' = 'polite',
    atomic: boolean = true
  ) => {
    const region = document.createElement('div');
    region.setAttribute('aria-live', type);
    region.setAttribute('aria-atomic', atomic.toString());
    region.className = 'sr-only';
    document.body.appendChild(region);
    regionRef.current = region;
    return region;
  }, []);

  const updateRegion = useCallback((content: string) => {
    if (!regionRef.current) {
      regionRef.current = createRegion();
    }
    regionRef.current.textContent = content;
  }, [createRegion]);

  const clearRegion = useCallback(() => {
    if (regionRef.current) {
      regionRef.current.textContent = '';
    }
  }, []);

  useEffect(() => {
    return () => {
      if (regionRef.current && regionRef.current.parentNode) {
        regionRef.current.parentNode.removeChild(regionRef.current);
      }
    };
  }, []);

  return { updateRegion, clearRegion };
}

// Focus management for modals and dialogs
export function useFocusManagement(isOpen: boolean, containerRef: React.RefObject<HTMLElement>) {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus first focusable element in container
      if (containerRef.current) {
        const focusable = containerRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        
        if (focusable) {
          focusable.focus();
        }
      }
    } else {
      // Restore previous focus
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    }
  }, [isOpen, containerRef]);
}

// Accessibility checklist hook
export function useA11yChecklist() {
  useEffect(() => {
    // Check for missing alt text
    const images = document.querySelectorAll('img:not([alt])');
    if (images.length > 0) {
      console.warn(`Found ${images.length} images without alt text`, images);
    }

    // Check for missing labels
    const inputs = document.querySelectorAll('input:not([aria-label]):not([id])');
    if (inputs.length > 0) {
      console.warn(`Found ${inputs.length} inputs without labels`, inputs);
    }

    // Check for color contrast (development only)
    if (process.env.NODE_ENV === 'development') {
      // This is a simplified check - for production use a proper contrast checker
      const lowContrastElements = document.querySelectorAll('[class*="text-gray-400"]');
      if (lowContrastElements.length > 0) {
        console.warn('Consider checking color contrast for gray-400 text elements');
      }
    }
  }, []);
}

// Voice control hook
export function useVoiceControl(commands: Record<string, () => void>) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'tr-TR';

    recognition.onresult = (event: any) => {
      const last = event.results.length - 1;
      const transcript = event.results[last][0].transcript.toLowerCase();

      // Check for commands
      Object.entries(commands).forEach(([command, action]) => {
        if (transcript.includes(command.toLowerCase())) {
          action();
        }
      });
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [commands]);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  return { isListening, toggleListening };
}

// Import React hooks
import { useState } from 'react';